import React, { useState } from 'react'
import { ShieldCheckIcon, ChevronDownIcon, ChevronUpIcon, EyeIcon } from '@heroicons/react/24/outline'

/**
 * ContentCredentials — Displays content provenance/authenticity info on the product page
 * 
 * Inspired by Adobe Content Authenticity but focused on the creator economy:
 * shows creation date, creator verification status, usage counts, and provenance chain.
 */
const ContentCredentials = ({ product, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!product) return null

  const {
    users_permissions_user: creator,
    vtoUsageCount = 0,
    arPlacementCount = 0,
    licensingRevenue = 0,
    creativeCategory,
    contentFingerprint,
    createdAt
  } = product

  // Only show if there's meaningful provenance data
  const hasProvenance = creator || contentFingerprint || vtoUsageCount > 0 || arPlacementCount > 0

  if (!hasProvenance) return null

  const totalUsages = vtoUsageCount + arPlacementCount
  const currency = product.currency || 'USD'
  const formattedRevenue = licensingRevenue > 0
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(licensingRevenue)
    : null

  const createdDate = createdAt
    ? new Date(createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null

  // Creator category label
  const categoryLabel = {
    fashion: 'Fashion Designer',
    painting: 'Painter',
    sculpture: 'Sculptor',
    digital_art: 'Digital Artist',
    photography: 'Photographer',
    illustration: 'Illustrator',
    textile_art: 'Textile Artist',
    mixed_media: 'Mixed Media Artist',
    other: 'Creator'
  }[creativeCategory] || 'Creator'

  return (
    <div className={`border-t border-gray-200 pt-6 ${className}`}>
      {/* Collapsed header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between group"
      >
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="h-4 w-4 text-teal-600" />
          <h3 className="text-sm font-medium text-gray-900">Content Credentials</h3>
          {contentFingerprint && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-teal-50 text-teal-700">
              Verified
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {totalUsages > 0 && (
            <span className="text-xs text-gray-400">{totalUsages.toLocaleString()} digital uses</span>
          )}
          {isExpanded
            ? <ChevronUpIcon className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
            : <ChevronDownIcon className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
          }
        </div>
      </button>

      {/* Expanded details */}
      {isExpanded && (
        <div className="mt-4 space-y-3 animate-in slide-in-from-top-1 duration-200">
          {/* Creator info */}
          {creator && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white text-xs font-bold">
                {(creator.username || 'C')[0].toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">@{creator.username || 'creator'}</p>
                <p className="text-xs text-gray-500">{categoryLabel}</p>
              </div>
              {creator.confirmed && (
                <span className="ml-auto text-xs text-teal-600 font-medium">✓ Verified</span>
              )}
            </div>
          )}

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-2">
            {createdDate && (
              <div className="p-2.5 rounded-lg bg-gray-50">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Created</p>
                <p className="text-sm font-medium text-gray-700 mt-0.5">{createdDate}</p>
              </div>
            )}
            {vtoUsageCount > 0 && (
              <div className="p-2.5 rounded-lg bg-purple-50">
                <p className="text-[10px] text-purple-400 uppercase tracking-wider">Virtual Try-Ons</p>
                <p className="text-sm font-medium text-purple-700 mt-0.5">{vtoUsageCount.toLocaleString()}</p>
              </div>
            )}
            {arPlacementCount > 0 && (
              <div className="p-2.5 rounded-lg bg-blue-50">
                <p className="text-[10px] text-blue-400 uppercase tracking-wider">AR Placements</p>
                <p className="text-sm font-medium text-blue-700 mt-0.5">{arPlacementCount.toLocaleString()}</p>
              </div>
            )}
            {formattedRevenue && (
              <div className="p-2.5 rounded-lg bg-green-50">
                <p className="text-[10px] text-green-400 uppercase tracking-wider">Creator Earnings</p>
                <p className="text-sm font-medium text-green-700 mt-0.5">{formattedRevenue}</p>
              </div>
            )}
          </div>

          {/* Fingerprint info */}
          {contentFingerprint && (
            <div className="flex items-center gap-2 px-3 py-2 rounded bg-gray-50 text-xs text-gray-500">
              <EyeIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="font-mono truncate">
                Fingerprint: {contentFingerprint.substring(0, 16)}...
              </span>
            </div>
          )}

          {/* Philosophy note */}
          <p className="text-[11px] text-gray-400 leading-relaxed">
            Every digital interaction with this work is recorded. The creator earns revenue
            when their design is used in virtual try-ons, AR placements, and AI-assisted creation.
          </p>
        </div>
      )}
    </div>
  )
}

export default ContentCredentials
